import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileText, Plus, Trash2, Loader2 } from "lucide-react";

const CRIMSON = "var(--brand)";

export default function HrNoteTypes() {
  const [types, setTypes] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.hrNoteTypes().then(setTypes).catch(() => setTypes([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!label.trim()) { toast.error("Enter a note type."); return; }
    setBusy(true);
    try { await api.hrNoteTypeAdd(label.trim()); setLabel(""); toast.success("Added."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add."); }
    finally { setBusy(false); }
  };
  const del = async (t) => {
    if (!window.confirm(`Remove the "${t.label}" note type? Existing notes keep their label.`)) return;
    try { await api.hrNoteTypeDelete(t.id); toast.success("Removed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove."); }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="hr-note-types-page">
      <div className="flex items-center gap-2.5">
        <FileText className="h-5 w-5" style={{ color: CRIMSON }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">HR Note Types</h1>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-5">
        These are the categories available when adding a management note on a staff member's Notes tab in HR → Staff Records.
        Default types are seeded automatically; you can add or remove as needed.
      </p>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex gap-2 mb-4">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="New note type (e.g. Grievance)"
            className="h-9 text-sm"
            data-testid="hr-note-type-input"
          />
          <Button size="sm" className="h-9 shrink-0 text-white" style={{ backgroundColor: CRIMSON }} onClick={add} disabled={busy} data-testid="hr-note-type-add">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}Add
          </Button>
        </div>

        {types === null ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : types.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400">No note types yet — add one above.</div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="hr-note-types-list">
            {types.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2.5" data-testid={`hr-note-type-${t.id}`}>
                <span className="text-sm font-medium text-slate-700">{t.label}</span>
                <button
                  onClick={() => del(t)}
                  className="text-slate-400 hover:text-rose-600 p-1.5 rounded-md hover:bg-rose-50"
                  title="Remove"
                  data-testid={`hr-note-type-del-${t.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
